import { Box, Card, Dialog, Stack } from '@mui/material';
import { ReactElement, useState } from 'react';
import {
  Button,
  CreateButton,
  Datagrid,
  DeleteButton,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { Iconify } from 'src/components/iconify';
import { XButton } from 'src/pages/admin/components/XButton';
import FilterGroup from 'src/pages/admin/components/FilterGroup';
import XTextInput from 'src/pages/admin/components/XTextInput';
import IdField from 'src/pages/admin/components/IdField';
import XDateField from 'src/pages/admin/components/XDateField';
import { EmptyContent } from 'src/components/empty-content';
import { Resources } from '../../resource';
import CreateLinkConfig from './Create';
import EditLinkConfig from './Edit';

const ListFilter = (props: any) => (
  <Filter {...(props || {})}>
    <FilterGroup alwaysOn direction="row">
      <XTextInput source="key" label="module.linkConfig.key" />
      <XTextInput source="value" label="module.linkConfig.value" />
    </FilterGroup>
  </Filter>
);

const View = () => {
  const translate = useTranslate();
  const [popup, setPopup] = useState<ReactElement>();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  return (
    <PermissionGuard resource="link-configs" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.linkConfig.title')}
          links={[
            {
              name: translate('module.linkConfig.title'),
              href: `/${Resources.linkConfig}`,
            },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <PermissionGuard resource="link-configs" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateLinkConfig onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(
                      `${translate('resource.auditLogs')} - ${translate('resource.linkConfig')}`
                    );
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            filters={<ListFilter />}
            perPage={10}
            exporter={false}
            resource={Resources.linkConfig}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                empty={<EmptyContent />}
                rowClick={false}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-thead th, & .RaDatagrid-tbody td': {
                    whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    verticalAlign: 'middle',
                  },
                }}
              >
                <FunctionField
                  label="ID"
                  render={(record: any) => <IdField id={record.id} />}
                />
                <TextField
                  source="key"
                  label="module.linkConfig.key"
                  sortable={false}
                />
                <TextField
                  source="value"
                  label="module.linkConfig.value"
                  sortable={false}
                />
                <FunctionField
                  label="module.linkConfig.createdAt"
                  render={(record: any) => (
                    <XDateField value={record.createdAt} fullDate />
                  )}
                />
                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2} onClick={(e) => e.stopPropagation()}>
                      <PermissionGuard resource="link-configs" action="update" fallback={<></>}>
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditLinkConfig
                                id={record.id}
                                onClose={() => setPopup(undefined)}
                              />
                            );
                          }}
                        />
                      </PermissionGuard>
                      <PermissionGuard resource="link-configs" action="delete" fallback={<></>}>
                        <DeleteButton mutationMode="pessimistic" />
                      </PermissionGuard>
                      <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                        <XButton
                          variant="text"
                          size="small"
                          color="secondary"
                          disabled={!record.hasAuditLog}
                          onClick={() => {
                            setAuditTitle(`Audit Logs - Key: ${record.key}`);
                            setSelectedRecord(record);
                            setAuditLogOpen(true);
                          }}
                          label={translate('resource.auditLogs')}
                        >
                          <Iconify icon="ph:note" />
                        </XButton>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>

        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          recordId={selectedRecord?.id}
          resource={Resources.linkConfig}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
